import { RotateCcw, Users } from "lucide-react";
import { portStyle } from "@/lib/api";

const SORTS = [
  { value: "downloads", label: "Most Downloaded" },
  { value: "rating", label: "Top Rated" },
  { value: "year", label: "Newest" },
  { value: "title", label: "A → Z" },
];

export const FilterSidebar = ({
  filters,
  category,
  sourcePort,
  multiplayer,
  sort,
  onChange,
  onReset,
}) => {
  return (
    <aside
      data-testid="filter-sidebar"
      className="w-full lg:w-64 shrink-0 space-y-6"
    >
      <div className="bg-[#121115] border border-[#2e2a36] rounded-lg p-4">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-pixel text-xs uppercase tracking-wider text-white">
            Filters
          </h2>
          <button
            data-testid="reset-filters-btn"
            onClick={onReset}
            className="flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider text-gray-500 hover:text-[#ef4444] transition-colors"
          >
            <RotateCcw className="w-3 h-3" />
            Reset
          </button>
        </div>

        {/* Sort */}
        <div className="mb-5">
          <label className="block text-[10px] font-mono tracking-widest uppercase text-gray-500 mb-2">
            Sort By
          </label>
          <select
            data-testid="sort-select"
            value={sort}
            onChange={(e) => onChange({ sort: e.target.value })}
            className="w-full bg-[#18161d] border border-[#2e2a36] focus:border-[#84cc16] outline-none rounded px-3 py-2 text-xs font-mono text-gray-200 transition-colors"
          >
            {SORTS.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>
        </div>

        {/* Categories */}
        <div className="mb-5">
          <label className="block text-[10px] font-mono tracking-widest uppercase text-gray-500 mb-2">
            Category
          </label>
          <div className="flex flex-wrap gap-1.5">
            {["All", ...(filters?.categories || [])].map((cat) => (
              <button
                key={cat}
                data-testid={`filter-category-${cat.toLowerCase().replace(/\s+/g, "-")}`}
                onClick={() => onChange({ category: cat })}
                className={`px-2.5 py-1.5 rounded font-mono text-[10px] uppercase tracking-wider border transition-all ${
                  category === cat
                    ? "bg-[#ef4444] border-[#ef4444] text-white"
                    : "bg-[#18161d] border-[#2e2a36] text-gray-400 hover:border-[#ef4444]/50 hover:text-gray-200"
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>

        {/* Source ports */}
        <div className="mb-5">
          <label className="block text-[10px] font-mono tracking-widest uppercase text-gray-500 mb-2">
            Source Port
          </label>
          <div className="space-y-1.5">
            {["All", ...(filters?.source_ports || [])].map((port) => {
              const active = sourcePort === port;
              const s = portStyle(port);
              return (
                <button
                  key={port}
                  data-testid={`filter-port-${port.toLowerCase()}`}
                  onClick={() => onChange({ sourcePort: port })}
                  className={`w-full flex items-center gap-2 px-2.5 py-1.5 rounded font-mono text-[11px] tracking-wide border transition-all text-left ${
                    active
                      ? "bg-[#18161d]"
                      : "bg-transparent border-transparent hover:bg-[#18161d]"
                  }`}
                  style={active ? { borderColor: s.border, color: s.text } : { color: "#9ca3af" }}
                >
                  <span
                    className="w-3 h-3 rounded-sm border shrink-0"
                    style={{
                      borderColor: active ? s.border : "#2e2a36",
                      background: active ? s.border : "transparent",
                    }}
                  />
                  {port}
                </button>
              );
            })}
          </div>
        </div>

        {/* Multiplayer */}
        <button
          data-testid="filter-multiplayer-toggle"
          onClick={() => onChange({ multiplayer: multiplayer ? null : true })}
          className={`w-full flex items-center justify-between px-3 py-2.5 rounded font-mono text-[11px] uppercase tracking-wider border transition-all ${
            multiplayer
              ? "bg-[#84cc16]/15 border-[#84cc16] text-[#a3e635]"
              : "bg-[#18161d] border-[#2e2a36] text-gray-400 hover:text-gray-200"
          }`}
        >
          <span className="flex items-center gap-2">
            <Users className="w-3.5 h-3.5" />
            Multiplayer
          </span>
          <span
            className={`w-8 h-4 rounded-full relative transition-colors ${
              multiplayer ? "bg-[#84cc16]" : "bg-[#2e2a36]"
            }`}
          >
            <span
              className={`absolute top-0.5 w-3 h-3 rounded-full bg-white transition-all ${
                multiplayer ? "left-4" : "left-0.5"
              }`}
            />
          </span>
        </button>
      </div>
    </aside>
  );
};
